import type { ReactNode } from 'react';
import { Box, CircularProgress, Fab } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus } from './useOnboarding';
import { isAllowedWhileOnboarding, ONBOARDING_PATH, RETURN_FLAG } from './paths';

/**
 * Opens the first-login onboarding by itself (medopd-docs 102): while a clinic admin's or doctor's first run is not
 * finished and a required step is still to do, every other screen sends them to the wizard (except the screens the
 * pending required steps link to). Runs after the password-change and two-step checks. Fails open — a status that
 * cannot be loaded never locks anyone out; the server's own rules apply regardless.
 */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data, isLoading, enabled } = useOnboardingStatus();

  if (!enabled) return <>{children}</>;
  if (isLoading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }
  if (data?.blocking && !isAllowedWhileOnboarding(location.pathname, data.allowedPaths)) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }
  return (
    <>
      {children}
      {location.pathname !== ONBOARDING_PATH && <BackToSetup />}
    </>
  );
}

function BackToSetup() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  let show = false;
  try {
    show = sessionStorage.getItem(RETURN_FLAG) === '1';
  } catch {
    show = false;
  }
  if (!show) return null;
  return (
    <Fab
      variant="extended"
      color="primary"
      onClick={() => navigate(ONBOARDING_PATH)}
      sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (th) => th.zIndex.snackbar - 1 }}
    >
      <ListChecks size={18} style={{ marginRight: 8 }} />
      {t('backToSetup')}
    </Fab>
  );
}
